<template>
  <div class="card-body p-xl-20">
    <div class="table-responsive border-0">
      <table class="table align-middle mb-0" style="table-layout: fixed; width: 100%;">
        <thead>
          <tr class="bg-light">
            <th scope="col" class="text-title fw-bold fs-13 py-2 ps-3 border-0 rounded-start" style="width: 45%;">
              {{ $t("pos.product") }}
            </th>
            <th scope="col" class="text-title fw-bold fs-13 py-2 text-center border-0" style="width: 25%;">
              {{ $t("pos.qty") }}
            </th>
            <th scope="col" class="text-title fw-bold fs-13 py-2 text-end border-0" style="width: 20%;">
              {{ $t("pos.subtotal") }}
            </th>
            <th scope="col" class="text-title fw-bold fs-13 py-2 text-end pe-3 border-0 rounded-end" style="width: 10%;">
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in stateStore.selectedProducts" :key="product.id" class="border-bottom-dashed">
            <td class="shadow-none lh-1 fs-13 fw-normal text-paragraph ps-3 border-0 py-3">
              <div class="fw-bold text-dark mb-1 text-truncate" :title="product.name">
                <span class="fs-13">{{ product.code }} - {{ product.name }}</span>
              </div>
              <div class="d-flex align-items-center text-muted gap-2 flex-wrap mt-1">
                <!-- Size Dropdown -->
                <div class="d-flex align-items-center">
                  <label>{{ $t("pos.size") }} : </label>
                  <select 
                    class="form-select border-0 bg-transparent text-secondary py-0 px-2 shadow-none variant-select rounded-pill" 
                    :value="product.size_id"
                    @change="(e: any) => updateVariation(product.id, 'size', e.target.value ? parseInt(e.target.value) : null)"
                  >
                    <option :value="null">{{ hasProductSizes(product) ? $t("pos.choose_size") : $t("common.none") }}</option>
                    <option v-for="size in getAvailableSizes(product)" :key="size.id" :value="size.id">
                      {{ size.name }}
                    </option>
                  </select>
                </div>
                
                <span class="fs-12 text-secondary">•</span>
                
                <!-- Preparation Dropdown -->
                <div class="d-flex align-items-center">
                  <label>{{ $t("pos.sugar") }} : </label>
                  <select 
                    class="form-select border-0 bg-transparent text-secondary py-0 px-2 shadow-none variant-select rounded-pill" 
                    :value="product.sugar_id"
                    @change="(e: any) => updateVariation(product.id, 'sugar', e.target.value ? parseInt(e.target.value) : null)"
                  >
                    <option v-if="product.type === 1" :value="null">{{ $t("common.none") }}</option>
                    <option v-for="sugar in sugars" :key="sugar.id" :value="sugar.id">
                      {{ sugar.name }}
                    </option>
                  </select>
                </div>
                <span
                  v-if="getPromotionLabel(product)"
                  class="badge rounded-pill text-bg-warning-subtle text-warning-emphasis border border-warning border-opacity-25"
                >
                  {{ getPromotionLabel(product) }}
                </span>
              </div>
            </td>
            <td class="shadow-none lh-1 fs-13 fw-normal text-paragraph border-0 py-3">
              <div class="d-flex align-items-center justify-content-center bg-transparent rounded-pill px-2 py-1 mx-auto" style="width: fit-content;">
                <button class="btn btn-outline-secondary border-0 p-0 d-flex align-items-center justify-content-center rounded-circle bg-light shadow-none" style="width: 26px; height: 26px; font-size: 14px; line-height: 1; padding-bottom: 2px !important;" @click="updateQty(product.id, product.qty - 1)">-</button>
                <span class="mx-3 fs-14 fw-bold text-dark">{{ product.qty }}</span>
                <button class="btn btn-outline-primary border-0 p-0 d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary shadow-none" style="width: 26px; height: 26px; font-size: 14px; line-height: 1;" @click="updateQty(product.id, product.qty + 1)">+</button>
              </div>
            </td>
            <td class="shadow-none lh-1 fs-14 fw-bold text-dark text-end border-0 py-3">
              {{ stateStore.formatPrice(getLineTotal(product)) }}
            </td>
            <td class="shadow-none lh-1 text-end pe-3 border-0 py-3">
              <button class="btn p-1 border-0 text-danger" @click="removeProduct(product.id)">
                <i class="ri-delete-bin-line fs-18"></i>
              </button>
            </td>
          </tr>
          <tr v-if="stateStore.selectedProducts.length === 0">
            <td colspan="4" class="text-center py-5 text-muted border-0">
              <i class="ri-shopping-cart-2-line fs-1 d-block mb-2 text-light"></i>
              {{ $t("pos.no_products_selected") }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import stateStore from "@/utils/store";
import { sizeService, type Size } from "@/services/size";
import { sugarService, type Sugar } from "@/services/sugar";
import { calculateLineDiscountAmount, calculateLineTotal } from "@/utils/posPricing";

export default defineComponent({
  name: "SelectedProducts",
  setup() {
    const { t } = useI18n();
    const sizes = ref<Size[]>([]);
    const sugars = ref<Sugar[]>([]);

    const fetchSizes = async () => {
      try {
        const response = await sizeService.getSizes();
        sizes.value = response;
      } catch (error) {
        console.error("Error fetching sizes:", error);
      }
    };

    const fetchSugars = async () => {
      try {
        const response = await sugarService.getSugars();
        sugars.value = response;
      } catch (error) {
        console.error("Error fetching preparation options:", error);
      }
    };

    const updateQty = (id: number, qty: number) => {
      if (qty < 1) return;
      stateStore.setProductQty(id, qty);
    };

    const updateVariation = (id: number, type: 'size' | 'sugar', val: number | null) => {
      stateStore.setProductVariation(id, type, val);
    };

    const removeProduct = (id: number) => {
      stateStore.removeSelectedProduct(id);
    };

    const getLineTotal = (product: any) =>
      calculateLineTotal({
        unitPrice: Number(product.price || 0),
        quantity: Number(product.qty || 0),
        discount: Number(product.discount || 0),
        discountType: product.discount_type,
      });

    const getPromotionLabel = (product: any) => {
      const discountAmount = calculateLineDiscountAmount({
        unitPrice: Number(product.price || 0),
        quantity: Number(product.qty || 0),
        discount: Number(product.discount || 0),
        discountType: product.discount_type,
      });

      if (discountAmount <= 0) return "";

      return product.discount_type === "percentage"
        ? t("pos.promo_percentage_off", {
            value: Number(product.discount || 0),
          })
        : t("pos.promo_amount_off", {
            amount: stateStore.formatPrice(discountAmount),
          });
    };

    const getAvailableSizes = (product: any) => {
      if (product.product_sizes && product.product_sizes.length > 0) {
        return product.product_sizes.map((ps: any) => ({
          id: ps.size_id,
          name: ps.size?.name || t("pos.size_with_id", { id: ps.size_id })
        }));
      }
      return sizes.value;
    };

    const hasProductSizes = (product: any) => {
      return Array.isArray(product.product_sizes) && product.product_sizes.length > 0;
    };

    const getSizeName = (id: number) => {
      const s = sizes.value.find(x => x.id === id);
      return s ? s.name : t("pos.size_with_id", { id });
    };

    const getSugarName = (id: number) => {
      const s = sugars.value.find(x => x.id === id);
      return s ? s.name : t("pos.sugar_with_id", { id });
    };

    onMounted(() => {
      fetchSizes();
      fetchSugars();
    });

    watch(() => stateStore.selectedProducts, (newProducts) => {
      newProducts.forEach(p => {
        if (p.type !== 1) {
          if (!hasProductSizes(p) && (p.size_id === null || p.size_id === undefined) && sizes.value.length > 0) {
            updateVariation(p.id, 'size', sizes.value[0].id || null);
          }
          if ((p.sugar_id === null || p.sugar_id === undefined) && sugars.value && sugars.value.length > 0) {
            updateVariation(p.id, 'sugar', sugars.value[0].id || null);
          }
        }
      });
    }, { deep: true, immediate: true });

    // Also watch preparation options/sizes to initialize them when data loads
    watch(sizes, () => {
      stateStore.selectedProducts.forEach(p => {
        if (p.type !== 1) {
          if (!hasProductSizes(p) && (p.size_id === null || p.size_id === undefined)) {
            const availSizes = getAvailableSizes(p);
            if (availSizes && availSizes.length > 0) {
              updateVariation(p.id, 'size', availSizes[0].id || null);
            }
          }
        }
      });
    }, { deep: true });

    watch(sugars, (newSugars) => {
      if (newSugars.length > 0) {
        stateStore.selectedProducts.forEach(p => {
          if (p.type !== 1) {
            if (p.sugar_id === null || p.sugar_id === undefined) {
              updateVariation(p.id, 'sugar', newSugars[0].id || null);
            }
          }
        });
      }
    }, { deep: true });

    return {
      stateStore,
      sizes,
      sugars,
      updateQty,
      updateVariation,
      removeProduct,
      getLineTotal,
      getPromotionLabel,
      getAvailableSizes,
      hasProductSizes,
      getSizeName,
      getSugarName,
    };
  },
});
</script>

<style scoped>
.variant-select {
  width: auto;
  cursor: pointer;
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  background-color: #f8f9fa !important;
  color: #64748b !important;
  border: 1px solid #e2e8f0 !important;
  transition: all 0.2s;
  padding-right: 32px !important; /* Space for the default arrow */
}
.variant-select:hover {
  background-color: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
  color: #334155 !important;
}

.border-bottom-dashed {
  border-bottom: 1px dashed #eee;
}
.table-responsive {
  overflow-x: hidden !important; /* Prevent horizontal scroll */
}
.card-body {
  padding: 15px !important;
}
</style>
